import React from 'react';
import { Calendar, ChevronDown, Clock, MapPin, Users } from 'lucide-react';

interface ShiftSummaryCardProps {
  shift: any;
  requiredCount: number;
  crewCount: number;
  acceptedCount: number;
  pendingCount: number;
  shortStaffed: boolean;
  needsApproval: boolean;
  awaitingReply: boolean;
  forceOpen: boolean;
  children: React.ReactNode;
}

export const ShiftSummaryCard: React.FC<ShiftSummaryCardProps> = ({
  shift,
  requiredCount,
  crewCount,
  acceptedCount,
  pendingCount,
  shortStaffed,
  needsApproval,
  awaitingReply,
  forceOpen,
  children,
}) => {
  let alertBorder = 'border-white/10';
  let alertGlow = '';
  if (needsApproval) {
    alertBorder = 'border-brand-neonBlue/50';
    alertGlow = 'shadow-[0_0_15px_rgba(0,243,255,0.15)]';
  } else if (shortStaffed && !awaitingReply) {
    alertBorder = 'border-rose-500/50';
    alertGlow = 'shadow-[0_0_15px_rgba(244,63,94,0.15)]';
  } else if (awaitingReply) {
    alertBorder = 'border-yellow-500/50';
    alertGlow = 'shadow-[0_0_15px_rgba(234,179,8,0.15)]';
  }

  const crewNeeded = Math.max(requiredCount - crewCount, 0);
  const operatingStart = shift.start_time || shift.setup_time;
  const operatingEnd = shift.end_time || shift.pack_down_time;
  const formatTime = (value?: string | null) => value ? String(value).slice(0, 5) : null;
  const operatingRange = operatingStart || operatingEnd
    ? `${formatTime(operatingStart) || 'TBD'}–${formatTime(operatingEnd) || 'TBD'}`
    : 'Time TBD';
  const status = needsApproval
    ? { label: `${acceptedCount} ${acceptedCount === 1 ? 'Reply' : 'Replies'} to confirm`, style: 'text-brand-neonBlue bg-brand-neonBlue/10' }
    : awaitingReply
      ? { label: 'Awaiting Replies', style: 'text-yellow-300 bg-yellow-500/10' }
      : shortStaffed
        ? { label: `${crewNeeded || 1} Crew Needed`, style: 'text-rose-300 bg-rose-500/10' }
        : { label: 'Ready', style: 'text-emerald-300 bg-emerald-500/10' };

  return (
    <details open={forceOpen ? true : undefined} className={`job-shift-card bg-slate-900/50 rounded-2xl border group/shift transition-all duration-300 ${alertBorder} ${alertGlow}`} data-testid="shift-summary-card">
      <summary className="cursor-pointer min-h-16 p-4 sm:p-5 select-none flex justify-between items-start outline-none hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-neonBlue transition-colors group/summary">
        <div className="flex-1 min-w-0 pr-3 sm:pr-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <h5 className="font-bold text-white text-base sm:text-lg group-open/shift:text-brand-neonPink transition-colors leading-snug break-words" data-testid="shift-title">{shift.shift_title || 'Untitled Shift'}</h5>
            <span className={`type-label self-start shrink-0 rounded-full px-2.5 py-1 ${status.style}`} data-testid="shift-operational-status">{status.label}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-xs font-semibold text-slate-400 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto]">
            <span className="flex items-center gap-1.5"><Calendar size={13} className="text-brand-neonBlue shrink-0" />{shift.event_date ? new Date(shift.event_date).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date TBD'}</span>
            <span className="flex items-start gap-1.5 min-w-0"><MapPin size={13} className="text-slate-500 shrink-0 mt-0.5" /><span className="break-words">{shift.venue_location || 'Venue TBD'}</span></span>
            <span className="flex items-center gap-1.5 shrink-0"><Clock size={13} className="text-slate-500" />{operatingRange}</span>
            <span className="flex items-center gap-1.5 shrink-0 text-brand-neonBlue"><Users size={13} /> {crewCount}/{requiredCount} Crew</span>
          </div>
        </div>
        <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0 group-hover/summary:bg-white/10 transition-colors"><ChevronDown size={18} className="text-slate-400 group-open/shift:rotate-180 transition-transform" /></div>
      </summary>
      {children}
    </details>
  );
};
